"use client";

import React from "react";
import dynamic from "next/dynamic";
import GlobeTweakPanel from "@/components/ui/globeTweakPanel";
import {
  COVERAGE_LOCATIONS,
  MAP_BASE,
} from "@/data/coverage";

const WorkMap = dynamic(() => import("@/components/ui/work-map"), {
  ssr: false,
  loading: () => (
    <div className="flex aspect-[2/1] min-h-0 w-full items-center justify-center bg-white lg:min-h-[560px]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
    </div>
  ),
});

const ROUTES = COVERAGE_LOCATIONS.map((location) => ({
  id: `england-${location.id}`,
  start: MAP_BASE,
  end: location,
}));

const DEFAULTS = {
  containerMinHeight: 560,
  backgroundColor: "#ffffff",
  mapDotColor: "#545454",
  mapDotOpacity: 0.31,
  mapDotRadius: 0.24,
  showGlow: true,
  glowColor: "#fee771",
  glowOpacity: 0.15,
  lineColor: "#FEC950",
  lineOpacity: 0,
  lineWidth: 1.48,
  pathArcHeight: 81,
  pathDuration: 2.29,
  pathDelayStep: 0.27,
  showEndpoints: true,
  endpointColor: "#FEC950",
  endpointRadius: 2.5,
  showEndpointPulse: false,
  endpointPulseColor: "#FEC950",
  endpointPulseOpacity: 0.34,
  endpointPulseStartRadius: 2.5,
  endpointPulseEndRadius: 5,
  endpointPulseDuration: 1.7,
  showLabels: true,
  labelScale: 0.9,
  labelFontSize: 11,
  labelOffsetY: 14,
  labelTextColor: "#ffffff",
  labelBackgroundColor: "#000000",
  labelBackgroundOpacity: 0.96,
  labelBorderColor: "#3b3b3b",
  labelBorderOpacity: 0,
};

export default function AceternityGlobeSection({
  className = "",
  tweak = false,
}) {
  const [settings, setSettings] = React.useState(DEFAULTS);
  const [shouldLoadMap, setShouldLoadMap] = React.useState(false);
  const sectionRef = React.useRef(null);

  React.useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    if (!("IntersectionObserver" in window)) {
      const timeoutId = window.setTimeout(() => setShouldLoadMap(true), 0);

      return () => window.clearTimeout(timeoutId);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        setShouldLoadMap(true);
        observer.disconnect();
      },
      { rootMargin: "600px 0px" },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`mx-auto w-full max-w-328  ${className}`}
    >
      <div className="mx-auto flex w-full items-center justify-center py-10 lg:py-16">
        <div className="relative w-full max-w">
          {shouldLoadMap ? (
            <WorkMap dots={ROUTES} config={settings} />
          ) : (
            <div
              aria-hidden="true"
              className="flex aspect-[2/1] min-h-0 w-full items-center justify-center bg-white lg:min-h-[560px]"
            >
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
            </div>
          )}

          {tweak && (
            <GlobeTweakPanel
              settings={settings}
              setSettings={setSettings}
              defaults={DEFAULTS}
            />
          )}
        </div>
      </div>
    </section>
  );
}
